<template>
  <div>
    <label class="font-medium text-primary dark:text-white" :class="label_class">{{ label }}</label>
    <input
      :id="id"
      :type="type"
      :name="name"
      :placeholder="placeholder"
      :class="['block mt-1 flex-1 border border-primary dark:border-white rounded-md px-3 py-2 text-primary dark:text-black placeholder:text-primary dark:placeholder:text-white focus:outline-none focus:ring-2 focus:ring-color-3 focus:border-transparent', input_class]"
      :value="formattedValue"
      :disabled="disable_input"
      @input="handleInput"
      :max="max"
      :min="min"
    />
    <Message v-for="(item, index) in hint" :key="index" size="small" severity="secondary" variant="simple">{{ item }}
    </Message>
    <Message v-if="!valid" size="small" severity="error" variant="simple">{{ errorMessage }}
    </Message>
  </div>
</template>

<script>
export default {
  name: 'InputDateTime',
  props: {
    label: {
      type: String,
      default: ''
    },
    label_class: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'date',
      validator: (value) => ['date', 'datetime-local', 'time', 'month'].includes(value)
    },
    name: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    input_class: {
      type: String,
      default: ''
    },
    modelValue: {
      type: [String, Date],
      default: ''
    },
    disable_input: {
      type: Boolean,
      default: false
    },
    max: {
      type: String,
      default: ''
    },
    min: {
      type: String,
      default: ''
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD',
      validator: (value) => ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm', 'HH:mm', 'YYYY-MM'].includes(value)
    },
    valid: {
      type: Boolean,
      default: true
    },
    errorMessage: {
      type: String,
      default: ''
    },
    hint: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  computed: {
    formattedValue() {
      if (!this.modelValue) return '';
      
      const date = new Date(this.modelValue);
      if (isNaN(date.getTime())) return this.modelValue;
      
      switch (this.type) {
        case 'datetime-local': {
          // Format for local display while preserving local time
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        }
        case 'time':
          return date.toTimeString().slice(0, 5);
        case 'month':
          return date.toISOString().slice(0, 7);
        default: // date
          return date.toISOString().split('T')[0];
      }
    }
  },
  methods: {
    handleInput(event) {
      // For datetime-local, we need to ensure we're passing the correct value to the backend
      // The input value will be in local time, which is what we want to display
      if (this.type === 'datetime-local' && event.target.value) {
        // Create a date object from the input value (which is in local time)
        const localDate = new Date(event.target.value);
        // Emit the ISO string which will be in UTC (for backend)
        this.$emit('update:modelValue', localDate.toISOString());
      } else {
        // For other types, just pass the value as is
        this.$emit('update:modelValue', event.target.value);
      }
    }
  }
};
</script>